// EventFormDialog — Create / Edit modal for a calendar Event (comp-ε
// GAP-3b-ii-b, CHG-2450). Canonical, hand-instantiated <FormDialog> driven by
// the v2 field-primitive registry — including the new ToggleField (CHG-2449)
// for the four boolean switches (is_public / all_day / is_virtual /
// registration_enabled). This is the canonical pattern now that the basic
// primitives (CHG-2433/2448) + ToggleField exist; no doctrine exception and no
// wizard-codegen is required (a future wizard-manifest generator could emit
// this — tracked low-priority in DRIFT-910).
//
// Field set tracks the EventCreate/EventUpdate schema
// (src/api/app/schemas/event.py) and the authoritative (v1-targeted)
// event-create.form.yaml / event-edit.form.yaml, closed incrementally per
// DRIFT-910. Slice 1 (CHG-2457) added slug + body + the `url` primitive. Slice 2
// (CHG-2462) added campaign_id + designation_id. Slice 3 (CHG-2478) adds the
// is_recurring toggle + cadence_rrule (rrule-picker) + recurrence_end_date, with
// the cadence + end gated on is_recurring (the v2 equivalent of v1's
// visible_when). Slice 4 (CHG-2483) adds hero_image_asset_id (asset-picker) —
// completing the buildable field set (only DRIFT-910's wizard-codegen sub-gap
// stays deferred).
//
// datetime: starts_at / ends_at use the registry `datetime` primitive — now the
// REAL DateTimeField (DRIFT-910 slice 1 / CHG-2457 promoted it off the CHG-1167
// stub onto the FieldShell a11y pattern). The ISO⇄datetime-local conversion at
// the form boundary (isoToDateTimeLocal / dateTimeLocalToIso) prevents the v1
// BUG-1059 blank-prefill regression on edit.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { FormDialog, type FormFieldDef } from "../composition/FormDialog";
import {
  eventsApi,
  type Event,
  type EventWritePayload,
} from "../../api/events";
import { campaignsApi } from "../../api/campaigns";
import type { SelectOption } from "../fields/SelectField";
import { isoToDateTimeLocal, dateTimeLocalToIso } from "../../lib/datetime-local";
import { parseFieldErrors } from "../../lib/parseFieldErrors";

// Event-type options mirror the authoritative event-create.form.yaml option
// list (the single source of truth across v1 create/edit + the list filter).
const EVENT_TYPE_OPTIONS = [
  { value: "worship", label: "Worship" },
  { value: "recurring_worship", label: "Recurring Worship" },
  { value: "community", label: "Community" },
  { value: "education", label: "Education" },
  { value: "volunteer", label: "Volunteer" },
  { value: "gala", label: "Gala" },
  { value: "announcement", label: "Announcement" },
  { value: "other", label: "Other" },
];

// Admin-settable lifecycle statuses. `upcoming` / `live` are time-derived by
// the backend and intentionally excluded from manual selection.
const EVENT_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
  { value: "canceled", label: "Canceled" },
  { value: "completed", label: "Completed" },
];

// Fields are built per-render so campaign_id's select options can be injected
// from the live campaigns query (DRIFT-910 slice 2). designation_id uses the
// self-fetching DesignationPicker primitive, so it needs no injected options.
function buildFields(
  campaignOptions: SelectOption[],
  isRecurring: boolean,
): FormFieldDef[] {
  return [
  { name: "name", type: "text", label: "Event name", required: true },
  {
    name: "slug",
    type: "text",
    label: "URL slug",
    helperText:
      "Public donate-page path segment, e.g. spring-gala. Leave blank to auto-generate from the name.",
  },
  {
    name: "event_type",
    type: "select",
    label: "Event type",
    required: true,
    props: { options: EVENT_TYPE_OPTIONS },
  },
  {
    name: "status",
    type: "select",
    label: "Status",
    required: true,
    props: { options: EVENT_STATUS_OPTIONS },
  },
  { name: "description", type: "textarea", label: "Description" },
  {
    name: "body",
    type: "textarea",
    label: "Public page body",
    helperText: "Long-form content shown on the event's public donate page.",
  },
  { name: "starts_at", type: "datetime", label: "Starts at" },
  { name: "ends_at", type: "datetime", label: "Ends at" },
  { name: "all_day", type: "toggle", label: "All day" },
  {
    name: "timezone",
    type: "text",
    label: "Timezone",
    helperText:
      "IANA timezone, e.g. America/New_York. Leave blank for the org default.",
  },
  // DRIFT-910 slice 3 — recurrence. is_recurring is a UI-only toggle that gates
  // the cadence picker + end date (the v2 equivalent of v1's
  // `visible_when: is_recurring == true`): the gated fields are only included in
  // the field set when the toggle is on, so a one-time event never renders (or
  // submits) a cadence. buildPayload strips is_recurring + nulls the cadence
  // when off (mirrors v1 EventDetail.tsx buildEventEditPayload, CHG-1505).
  {
    name: "is_recurring",
    type: "toggle",
    label: "Recurring event",
    helperText: "Enable to set a repeat cadence (e.g. weekly service, monthly meeting).",
  },
  ...(isRecurring
    ? ([
        {
          name: "cadence_rrule",
          type: "rrule-picker",
          label: "Recurrence cadence",
          helperText:
            "Set when this event repeats. Use Never / On date / After N to bound the series.",
        },
        {
          name: "recurrence_end_date",
          type: "date",
          label: "Recurrence end",
          helperText: "Optional hard stop for the series (separate from the cadence's own end).",
        },
      ] as FormFieldDef[])
    : []),
  { name: "is_public", type: "toggle", label: "Public" },
  { name: "location_name", type: "text", label: "Venue" },
  { name: "location_address", type: "text", label: "Address" },
  { name: "is_virtual", type: "toggle", label: "Virtual" },
  {
    name: "virtual_url",
    type: "url",
    label: "Virtual URL",
    helperText: "Streaming or video-call link (Zoom, Meet, etc.).",
  },
  { name: "registration_enabled", type: "toggle", label: "RSVP enabled" },
  {
    name: "max_attendees",
    type: "number",
    label: "Max attendees",
    props: { min: 1 },
  },
  { name: "registration_link", type: "url", label: "External RSVP URL" },
  {
    name: "campaign_id",
    type: "select",
    label: "Campaign",
    helperText: "Optionally link this event to a fundraising campaign.",
    props: { options: campaignOptions, placeholder: "No campaign" },
  },
  {
    name: "designation_id",
    type: "designation_picker",
    label: "Designation",
    helperText: "Optionally direct this event's giving to a designation.",
  },
  {
    name: "hero_image_asset_id",
    type: "asset-picker",
    label: "Hero image",
    helperText: "Optional banner image shown on the event's public donate page.",
  },
  ];
}

function initialValues(
  mode: "create" | "edit",
  event?: Event,
): Record<string, unknown> {
  if (mode === "edit" && event) {
    return {
      name: event.name ?? "",
      slug: event.slug ?? "",
      event_type: event.event_type ?? "other",
      status: event.status ?? "draft",
      description: event.description ?? "",
      body: event.body ?? "",
      starts_at: isoToDateTimeLocal(event.starts_at),
      ends_at: isoToDateTimeLocal(event.ends_at),
      all_day: event.all_day ?? false,
      timezone: event.timezone ?? "",
      is_public: event.is_public ?? true,
      location_name: event.location_name ?? "",
      location_address: event.location_address ?? "",
      is_virtual: event.is_virtual ?? false,
      virtual_url: event.virtual_url ?? "",
      registration_enabled: event.registration_enabled ?? false,
      max_attendees: event.max_attendees ?? null,
      registration_link: event.registration_link ?? "",
      campaign_id: event.campaign_id ?? "",
      designation_id: event.designation_id ?? "",
      // is_recurring is derived (no backend column): the event recurs if it has
      // a cadence_rrule or a non-`none` recurrence enum.
      is_recurring: Boolean(
        event.cadence_rrule ||
          (event.recurrence && event.recurrence !== "none"),
      ),
      cadence_rrule: event.cadence_rrule ?? "",
      recurrence_end_date: event.recurrence_end_date ?? "",
      hero_image_asset_id: event.hero_image_asset_id ?? "",
    };
  }
  // create defaults — mirror the EventCreate schema defaults.
  return {
    name: "",
    slug: "",
    event_type: "other",
    status: "draft",
    description: "",
    body: "",
    starts_at: "",
    ends_at: "",
    all_day: false,
    timezone: "",
    is_public: true,
    location_name: "",
    location_address: "",
    is_virtual: false,
    virtual_url: "",
    registration_enabled: false,
    max_attendees: null,
    registration_link: "",
    campaign_id: "",
    designation_id: "",
    is_recurring: false,
    cadence_rrule: "",
    recurrence_end_date: "",
    hero_image_asset_id: "",
  };
}

// Empty string → null for optional text fields; pass through otherwise.
function nullable(v: unknown): string | null {
  if (typeof v !== "string") return (v as string | null) ?? null;
  const t = v.trim();
  return t === "" ? null : t;
}

function buildPayload(values: Record<string, unknown>): EventWritePayload {
  const rawMax = values.max_attendees;
  const max =
    rawMax == null || rawMax === ""
      ? null
      : typeof rawMax === "number"
        ? rawMax
        : Number(rawMax);
  return {
    name: (values.name as string) ?? "",
    slug: nullable(values.slug),
    event_type: (values.event_type as string) || "other",
    status: (values.status as string) || "draft",
    description: nullable(values.description),
    body: nullable(values.body),
    starts_at: dateTimeLocalToIso(values.starts_at as string | null),
    ends_at: dateTimeLocalToIso(values.ends_at as string | null),
    all_day: Boolean(values.all_day),
    timezone: nullable(values.timezone),
    is_public: Boolean(values.is_public),
    location_name: nullable(values.location_name),
    location_address: nullable(values.location_address),
    is_virtual: Boolean(values.is_virtual),
    virtual_url: nullable(values.virtual_url),
    registration_enabled: Boolean(values.registration_enabled),
    max_attendees: max != null && Number.isFinite(max) ? max : null,
    registration_link: nullable(values.registration_link),
    campaign_id: nullable(values.campaign_id),
    designation_id: nullable(values.designation_id),
    hero_image_asset_id: nullable(values.hero_image_asset_id),
    // DRIFT-910 slice 3 — recurrence. is_recurring is UI-only (never sent). When
    // off, null the cadence + end date and set recurrence='none' so a previously
    // recurring event is cleared (mirrors v1 EventDetail.tsx buildEventEditPayload,
    // CHG-1505, which nulls all three). When on, send the cadence_rrule + end
    // date; `recurrence` is omitted (undefined → dropped by JSON), leaving the
    // backend default/existing enum. Omitting the enum is safe because it's
    // v1-faithful (the v1 create form has no recurrence-enum input either) AND the
    // donor-facing surfaces key on cadence_rrule directly — the iCal feed emits
    // RRULE:{cadence_rrule} and the calendar list-query expands it (DRIFT-678).
    // (Backend nuance: the _expand_recurrence call-site gates on
    // recurrence != none; cadence_rrule only selects the expansion METHOD.)
    ...(values.is_recurring
      ? {
          cadence_rrule: nullable(values.cadence_rrule),
          recurrence_end_date: nullable(values.recurrence_end_date),
        }
      : {
          recurrence: "none",
          cadence_rrule: null,
          recurrence_end_date: null,
        }),
  };
}

export function EventFormDialog({
  mode,
  event,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  event?: Event;
  onClose: () => void;
  onSaved: (saved: Event) => void;
}) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    initialValues(mode, event),
  );
  const [error, setError] = useState<string | undefined>(undefined);
  // DRIFT-942 / DRIFT-912 — per-field validation errors keyed by field name,
  // forwarded to FormDialog's `errors` prop so the field primitives surface
  // their role="alert" + aria-invalid + aria-describedby branch.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Campaign options for the campaign_id select (DRIFT-910 slice 2). Unfiltered
  // by status — draft/active campaigns are valid admin link targets; name-sorted.
  const { data: campaignData } = useQuery({
    queryKey: ["campaigns", "event-form-options"],
    queryFn: () =>
      campaignsApi.listCampaigns({
        pageSize: 200,
        sortField: "name",
        sortDirection: "asc",
      }),
  });
  const campaignOptions: SelectOption[] = useMemo(
    () =>
      (campaignData?.items ?? []).map((c) => ({ value: c.id, label: c.name })),
    [campaignData],
  );
  const isRecurring = Boolean(values.is_recurring);
  const fields = useMemo(
    () => buildFields(campaignOptions, isRecurring),
    [campaignOptions, isRecurring],
  );

  // The rendered field names — passed to parseFieldErrors so a backend
  // field_errors[] entry is routed per-field when its field_id matches a
  // rendered field, and rolled up to the banner otherwise.
  const knownFieldNames = useMemo(() => fields.map((f) => f.name), [fields]);

  const onChange = (name: string, value: unknown) => {
    setValues((v) => ({ ...v, [name]: value }));
    // Clear a field's server error as the user edits it (matches v1's
    // edit-clears-error UX so a stale 422 message doesn't persist).
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const applyError = (e: unknown, fallback: string) => {
    const parsed = parseFieldErrors(e, knownFieldNames);
    setFieldErrors(parsed.fieldErrors);
    // Banner: top-level detail / orphan rollup, else a generic fallback only
    // when there are no per-field errors to point the user at.
    setError(
      parsed.bannerMessage ??
        (parsed.hadFieldErrors ? undefined : fallback),
    );
  };

  const create = useMutation({
    mutationFn: (payload: EventWritePayload) => eventsApi.createEvent(payload),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      onSaved(saved);
    },
    onError: (e: unknown) => applyError(e, "Could not create event."),
  });

  const update = useMutation({
    mutationFn: (payload: EventWritePayload) =>
      eventsApi.updateEvent(event!.id, payload),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      onSaved(saved);
    },
    onError: (e: unknown) => applyError(e, "Could not save changes."),
  });

  const busy = create.isPending || update.isPending;

  const onSubmit = (submitted: Record<string, unknown>) => {
    setError(undefined);
    setFieldErrors({});
    const payload = buildPayload(submitted);
    if (mode === "create") create.mutate(payload);
    else update.mutate(payload);
  };

  return (
    <FormDialog
      open
      onClose={onClose}
      title={mode === "create" ? "Create event" : `Edit event — ${event?.name ?? ""}`}
      size="lg"
      fields={fields}
      values={values}
      onChange={onChange}
      onSubmit={onSubmit}
      submitLabel={mode === "create" ? "Create event" : "Save changes"}
      busy={busy}
      error={error}
      errors={fieldErrors}
    />
  );
}
